  import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiFetch from '../utils/api';
import { useAuth } from './AuthContext';

const StatsContext = createContext(null);

export const useStats = () => {
  const context = useContext(StatsContext);
  if (!context) throw new Error('useStats must be used within StatsProvider');
  return context;
};

export const StatsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [pins, setPins] = useState(null);
  const [treeData, setTreeData] = useState(null);
  
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [loadingPins, setLoadingPins] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);

  const fetchStats = useCallback(async (force = false) => {
    if (!isAuthenticated) return;
    if (stats && !force) return;
    setLoadingStats(true);
    try {
      const res = await apiFetch('/api/dashboard/stats');
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [isAuthenticated, stats]);

  const fetchRewards = useCallback(async (force = false) => {
    if (!isAuthenticated) return;
    if (rewards && !force) return;
    setLoadingRewards(true);
    try {
      const res = await apiFetch('/api/rewards');
      const data = await res.json();
      setRewards(data);
    } catch (err) {
      console.error('Failed to fetch rewards:', err);
    } finally {
      setLoadingRewards(false);
    }
  }, [isAuthenticated, rewards]);

  const fetchPins = useCallback(async (force = false) => {
    if (!isAuthenticated) return;
    if (pins && !force) return;
    setLoadingPins(true);
    try {
      const res = await apiFetch('/api/pin-codes?limit=100');
      const data = await res.json();
      setPins(data);
    } catch (err) {
      console.error('Failed to fetch pins:', err);
    } finally {
      setLoadingPins(false);
    }
  }, [isAuthenticated, pins]);

  const fetchTree = useCallback(async (force = false) => {
    if (!isAuthenticated) return;
    if (treeData && !force) return;
    setLoadingTree(true);
    try {
      const res = await apiFetch('/api/tree');
      const data = await res.json();
      setTreeData(data);
    } catch (err) {
      console.error('Failed to fetch tree:', err);
    } finally {
      setLoadingTree(false);
    }
  }, [isAuthenticated, treeData]);

  // Initial load when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchRewards();
      fetchPins();
      fetchTree();
    } else {
      // Clear data on logout
      setStats(null);
      setRewards(null);
      setPins(null);
      setTreeData(null);
    }
  }, [isAuthenticated, fetchStats, fetchRewards, fetchPins, fetchTree]);

  const refreshAll = () => {
    fetchStats(true);
    fetchRewards(true);
    fetchPins(true);
    fetchTree(true);
  };

  return (
    <StatsContext.Provider value={{
      stats, loadingStats, refreshStats: () => fetchStats(true),
      rewards, loadingRewards, refreshRewards: () => fetchRewards(true),
      pins, loadingPins, refreshPins: () => fetchPins(true),
      treeData, loadingTree, refreshTree: () => fetchTree(true),
      refreshAll
    }}>
      {children}
    </StatsContext.Provider>
  );
};

export default StatsContext;
